import { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import { ThemeProvider } from "./context/ThemeContext";
import IPProtection, { checkAccessGranted, shouldBypassProtection } from "./components/IPProtection";
const PublicProfile = lazy(() => import("./pages/shared/PublicProfile"));
// Eager import — was previously lazy but lazy chunks were getting
// stuck on the Suspense fallback in this dev environment. Moving it
// to the main bundle so /tutors loads instantly without spinner.
import TutorDirectory from "./pages/TutorDirectory";
// Story page — standalone editorial route at /story, no nav link.
import StoryPage from "./pages/StoryPage";
import MaintenanceGuard from "./components/MaintenanceGuard";
import TedChat from "./components/TedChat";
import MobileBackButton from "./components/MobileBackButton";
import MobileBottomTabBar from "./components/MobileBottomTabBar";
import LegalAgreementGate from "./components/LegalAgreementGate";
import InstallAppPrompt from "./components/InstallAppPrompt";
import KeyboardShortcutsOverlay from "./components/KeyboardShortcutsOverlay";
import SpaceBackdrop from "./components/SpaceBackdrop"; // eslint-disable-line no-unused-vars -- kept for one-line restoration
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import CanonicalTag from "./components/seo/CanonicalTag";
import AccessibilityPanel from "./components/a11y/AccessibilityPanel";
import { AccessibilityProvider } from "./context/AccessibilityContext";
import "./styles/accessibility.css";

// Critical pages - loaded immediately
import LandingPage from "./pages/LandingPage";
import UnifiedLoginPage from "./pages/UnifiedLoginPage";
import RegisterPage from "./pages/RegisterPage";

// Auth Pages
import StudentAuthPage from "./pages/auth/StudentAuthPage";
import ParentAuthPage from "./pages/auth/ParentAuthPage";
import TeacherAuthPage from "./pages/auth/TeacherAuthPage";
import AdminAuthPage from "./pages/auth/AdminAuthPage";

// Lazy loaded pages - loaded on demand
const LoginPage = lazy(() => import("./pages/LoginPage"));
const ChildLoginPage = lazy(() => import("./pages/ChildLoginPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const DocumentationPage = lazy(() => import("./pages/DocumentationPage"));
const FreePeriodWelcome = lazy(() => import("./pages/FreePeriodWelcome"));
import FreePeriodBanner from "./components/FreePeriodBanner";
import LanguageGreetingBanner from "./components/LanguageGreetingBanner";
import LockdownInterceptor from "./components/LockdownInterceptor";
import InactivityWarning from "./components/InactivityWarning";

// Student Pages - Lazy
const StudentDashboard = lazy(() => import("./pages/student/StudentDashboard"));
const SearchTeachers = lazy(() => import("./pages/student/SearchTeachers"));
const TeacherProfile = lazy(() => import("./pages/student/TeacherProfile"));
const StudentBookings = lazy(() => import("./pages/student/StudentBookings"));
const StudentMessages = lazy(() => import("./pages/student/StudentMessages"));
const LessonReports = lazy(() => import("./pages/student/LessonReports"));
const LessonReportDetail = lazy(() => import("./pages/student/LessonReportDetail"));
const StudentCalendar = lazy(() => import("./pages/student/StudentCalendar"));
const InstantLesson = lazy(() => import("./pages/student/InstantLesson"));
const BookLesson = lazy(() => import("./pages/student/BookLessonNew"));
const RequestSpecialist = lazy(() => import("./pages/student/RequestSpecialist"));
const AccessibilityStatement = lazy(() => import("./pages/AccessibilityStatement"));
const ParentResponsibilities = lazy(() => import("./pages/ParentResponsibilities"));
const TrustHub = lazy(() => import("./pages/TrustHub"));

// Teacher Pages - Lazy
const TeacherDashboard = lazy(() => import("./pages/teacher/TeacherDashboard"));
const TeacherProfileSetup = lazy(() => import("./pages/teacher/TeacherProfileSetup"));
const TeacherBookings = lazy(() => import("./pages/teacher/TeacherBookings"));
const TeacherEarnings = lazy(() => import("./pages/teacher/TeacherEarnings"));
const TeacherMessages = lazy(() => import("./pages/teacher/TeacherMessages"));
const TeacherCalendar = lazy(() => import("./pages/teacher/TeacherCalendar"));
const TeacherAvailability = lazy(() => import("./pages/teacher/TeacherAvailabilityNew"));
const TeacherPackages = lazy(() => import("./pages/teacher/TeacherPackages"));
const TeacherWithdrawals = lazy(() => import("./pages/teacher/TeacherWithdrawals"));
const TeacherFreeLessons = lazy(() => import("./pages/teacher/TeacherFreeLessons"));
const TeacherMedia = lazy(() => import("./pages/teacher/TeacherMedia"));
const TeacherProfilePreview = lazy(() => import("./pages/teacher/TeacherProfilePreview"));
const TeacherSessionSummaries = lazy(() => import("./pages/teacher/TeacherSessionSummaries"));
const TeacherRecordings = lazy(() => import("./pages/teacher/TeacherRecordings"));

// Admin Pages - Lazy
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminTeachers = lazy(() => import("./pages/admin/AdminTeachers"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminSubjects = lazy(() => import("./pages/admin/AdminSubjects"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminClassrooms = lazy(() => import("./pages/admin/AdminClassrooms"));
const AdminTrialSettings = lazy(() => import("./pages/admin/AdminTrialSettings"));
const AdminInvoices = lazy(() => import("./pages/admin/AdminInvoices"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminSecurityLogs = lazy(() => import("./pages/admin/AdminSecurityLogs"));
const AdminAnnouncements = lazy(() => import("./pages/admin/AdminAnnouncements"));
const AdminRefunds = lazy(() => import("./pages/admin/AdminRefunds"));
const AdminWithdrawals = lazy(() => import("./pages/admin/AdminWithdrawals"));
const AdminEmailLogs = lazy(() => import("./pages/admin/AdminEmailLogs"));
const AdminDemoBroadcast = lazy(() => import("./pages/admin/AdminDemoBroadcast"));
const AdminLibrary = lazy(() => import("./pages/admin/AdminLibrary"));
const AdminRecordings = lazy(() => import("./pages/admin/AdminRecordings"));
const AdminTerms = lazy(() => import("./pages/admin/AdminTerms"));
const AdminPrivacyPolicy = lazy(() => import("./pages/admin/AdminPrivacyPolicy"));
const AdminSystemHealth = lazy(() => import("./pages/admin/AdminSystemHealth"));
const AdminFeatures = lazy(() => import("./pages/admin/AdminFeatures"));
const AdminWaitlist = lazy(() => import("./pages/admin/AdminWaitlist"));

const TeacherSubjects = lazy(() => import("./pages/teacher/TeacherSubjects"));

// Parent Pages - Lazy
const ParentDashboard = lazy(() => import("./pages/parent/ParentDashboard"));
const AddChild = lazy(() => import("./pages/parent/AddChild"));
const ChildDetails = lazy(() => import("./pages/parent/ChildDetails"));
const ParentReports = lazy(() => import("./pages/parent/ParentReports"));
const ParentRecordings = lazy(() => import("./pages/parent/ParentRecordings"));
const ParentWeeklyReport = lazy(() => import("./pages/parent/ParentWeeklyReport"));

// Shared Pages - Lazy
const UserMessages = lazy(() => import("./pages/shared/UserMessages"));
const UserInvoices = lazy(() => import("./pages/shared/UserInvoices"));
const VirtualClassroom = lazy(() => import("./pages/shared/VirtualClassroom"));
const ParentMonitor = lazy(() => import("./pages/shared/ParentMonitor"));
const StealthMonitor = lazy(() => import("./pages/shared/StealthMonitor"));
const SubmitComplaint = lazy(() => import("./pages/shared/SubmitComplaint"));
const AdminComplaints = lazy(() => import("./pages/admin/AdminComplaints"));
const AdminAISecurity = lazy(() => import("./pages/admin/AdminAISecurity"));
const SecurityCommandCenter = lazy(() => import("./pages/admin/SecurityCommandCenter"));
const EmailLogsAdmin        = lazy(() => import("./pages/admin/EmailLogsAdmin"));
const TrustPage             = lazy(() => import("./pages/TrustPage"));
const AdminTrustPage        = lazy(() => import("./pages/admin/AdminTrustPage"));
const AdminBookingsV2Page   = lazy(() => import("./pages/admin/AdminBookingsV2Page"));
const BillingPage           = lazy(() => import("./pages/BillingPage"));
const BookV2Page            = lazy(() => import("./pages/BookV2Page"));
const AvailabilityManagerPage = lazy(() => import("./pages/AvailabilityManagerPage"));

// Components
import InteractiveTour from "./components/InteractiveTour";

// Payment Pages
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";

// Legal Pages
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import SubscriptionCancel from "./pages/SubscriptionCancel";
import AboutPage from "./pages/AboutPage";

// Subscription Block Component
const SubscriptionBlock = () => {
  const { language, t, localize } = useLanguage();
  
  return (
    <div className="min-h-screen gradient-hero flex items-center justify-center p-6">
      <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md text-center">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
          <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-4">
          {localize({ ar: 'انتهت الفترة التجريبية', en: 'Trial Period Ended' })}
        </h2>
        <p className="text-slate-600 mb-6">
          {t('subscriptionRequired')}
        </p>
        <a 
          href="/subscription"
          className="inline-block w-full py-3 px-6 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full font-medium transition-colors"
        >
          {t('renewSubscription')}
        </a>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, subscriptionValid, subscriptionStatus } = useAuth();
  const { t, localize } = useLanguage();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-hero">
        <div className="animate-pulse text-emerald-500 text-xl">{t('loading')}</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  // Check subscription for non-admin users
  if (user.role !== 'admin' && !subscriptionValid && subscriptionStatus !== 'trial') {
    return <SubscriptionBlock />;
  }
  
  return <LegalAgreementGate>{children}</LegalAgreementGate>;
};

function AppRoutes() {
  return (
    <>
      <CanonicalTag />
    <Routes>
      {/* Public Routes */}
      {/* Root → Trust Hub corporate portal (gates LT behind partner access code). */}
      <Route path="/" element={<TrustHub />} />
      {/* LWT marketing landing — accessed via the LT tile or direct link by partners. */}
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/tutors" element={<TutorDirectory />} />
      <Route path="/browse" element={<TutorDirectory />} />
      <Route path="/story" element={<StoryPage />} />
      <Route path="/trust" element={<TrustPage />} />
      <Route path="/login" element={<UnifiedLoginPage />} />
      <Route path="/login/old" element={<LoginPage />} />
      <Route path="/login/student" element={<ChildLoginPage />} />
      
      {/* Role-specific Auth Routes */}
      <Route path="/login/student-auth" element={<StudentAuthPage />} />
      <Route path="/login/parent-auth" element={<ParentAuthPage />} />
      <Route path="/login/teacher-auth" element={<TeacherAuthPage />} />
      <Route path="/login/admin-auth" element={<AdminAuthPage />} />
      
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/subscription" element={<SubscriptionPage />} />
      <Route path="/account/billing" element={
        <ProtectedRoute allowedRoles={["student", "teacher", "parent", "admin"]}>
          <BillingPage />
        </ProtectedRoute>
      } />
      <Route path="/book/:teacherId" element={<BookV2Page />} />
      <Route path="/teacher/availability" element={
        <ProtectedRoute allowedRoles={["teacher", "admin"]}>
          <AvailabilityManagerPage />
        </ProtectedRoute>
      } />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/accessibility" element={<AccessibilityStatement />} />
      <Route path="/parent-responsibilities" element={<ParentResponsibilities />} />
      <Route path="/trust-hub" element={<TrustHub />} />
      
      {/* Legal Pages */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/docs" element={<DocumentationPage />} />
      <Route path="/documentation" element={<DocumentationPage />} />
      
      {/* Free Period Page */}
      <Route path="/free" element={<FreePeriodWelcome />} />
      
      {/* Student Routes */}
      <Route path="/student" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <StudentDashboard />
        </ProtectedRoute>
      } />
      <Route path="/student/search" element={
        <ProtectedRoute allowedRoles={["student", "parent"]}>
          <SearchTeachers />
        </ProtectedRoute>
      } />
      <Route path="/student/teacher/:teacherId" element={
        <ProtectedRoute allowedRoles={["student", "teacher", "admin", "parent"]}>
          <TeacherProfile />
        </ProtectedRoute>
      } />
      <Route path="/student/bookings" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <StudentBookings />
        </ProtectedRoute>
      } />
      <Route path="/student/messages" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <StudentMessages />
        </ProtectedRoute>
      } />
      <Route path="/student/calendar" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <StudentCalendar />
        </ProtectedRoute>
      } />
      <Route path="/student/instant-lesson" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <InstantLesson />
        </ProtectedRoute>
      } />
      <Route path="/student/book/:teacherId" element={
        <ProtectedRoute allowedRoles={["student", "parent"]}>
          <BookLesson />
        </ProtectedRoute>
      } />
      <Route path="/student/request-specialist" element={
        <ProtectedRoute allowedRoles={["student"]}>
          <RequestSpecialist />
        </ProtectedRoute>
      } />
      <Route path="/student/reports" element={
        <ProtectedRoute allowedRoles={["student", "child"]}>
          <LessonReports />
        </ProtectedRoute>
      } />
      <Route path="/student/report/:bookingId" element={
        <ProtectedRoute allowedRoles={["student", "child"]}>
          <LessonReportDetail />
        </ProtectedRoute>
      } />
      
      {/* Teacher Routes */}
      <Route path="/teacher" element={
        <ProtectedRoute allowedRoles={["teacher"]}>
          <TeacherDashboard />
        </ProtectedRoute>
      } />
      <Route path="/teacher/profile" element={
        <ProtectedRoute allowedRoles={["teacher"]}>
          <TeacherProfileSetup />
        </ProtectedRoute>
      } />
      <Route path="/teacher/preview" element={
        <ProtectedRoute allowedRoles={["teacher"]}>
          <TeacherProfilePreview />
        </ProtectedRoute>
      } />
      <Route path="/teacher/bookings" element={
        <ProtectedRoute allowedRoles={["teacher"]}>
          <TeacherBookings />
        </ProtectedRoute>
      } />
      <Route path="/teacher/earnings" element={
        <ProtectedRoute allowedRoles={["teacher"]}>
          <TeacherEarnings />
        </ProtectedRoute>
      } />
      <Route path="/teacher/messages" element={
        <ProtectedRoute allowedRoles={["teacher"]}>
          <TeacherMessages />
        </ProtectedRoute>
      } />
      <Route path="/teacher/calendar" element={
        <ProtectedRoute allowedRoles={["teacher"]}>
          <TeacherCalendar />
        </ProtectedRoute>
      } />
      <Route path="/teacher/availability" element={
        <ProtectedRoute allowedRoles={["teacher"]}>
          <TeacherAvailability />
        </ProtectedRoute>
      } />
      <Route path="/teacher/packages" element={
        <ProtectedRoute allowedRoles={["teacher"]}>
          <TeacherPackages />
        </ProtectedRoute>
      } />
      <Route path="/teacher/subjects" element={
        <ProtectedRoute allowedRoles={["teacher"]}>
          <TeacherSubjects />
        </ProtectedRoute>
      } />
      <Route path="/teacher/withdrawals" element={
        <ProtectedRoute allowedRoles={["teacher"]}>
          <TeacherWithdrawals />
        </ProtectedRoute>
      } />
      <Route path="/teacher/free-lessons" element={
        <ProtectedRoute allowedRoles={["teacher"]}>
          <TeacherFreeLessons />
        </ProtectedRoute>
      } />
      <Route path="/teacher/media" element={
        <ProtectedRoute allowedRoles={["teacher"]}>
          <TeacherMedia />
        </ProtectedRoute>
      } />
      <Route path="/teacher/summaries" element={
        <ProtectedRoute allowedRoles={["teacher"]}>
          <TeacherSessionSummaries />
        </ProtectedRoute>
      } />
      <Route path="/teacher/recordings" element={
        <ProtectedRoute allowedRoles={["teacher"]}>
          <TeacherRecordings />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/teachers" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminTeachers />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminUsers />
        </ProtectedRoute>
      } />
      <Route path="/admin/subjects" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminSubjects />
        </ProtectedRoute>
      } />
      <Route path="/admin/reviews" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminReviews />
        </ProtectedRoute>
      } />
      <Route path="/admin/reports" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminReports />
        </ProtectedRoute>
      } />
      <Route path="/admin/messages" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminMessages />
        </ProtectedRoute>
      } />
      <Route path="/admin/classrooms" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminClassrooms />
        </ProtectedRoute>
      } />
      <Route path="/admin/library" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminLibrary />
        </ProtectedRoute>
      } />
      <Route path="/admin/recordings" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminRecordings />
        </ProtectedRoute>
      } />
      <Route path="/admin/trial-settings" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminTrialSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/invoices" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminInvoices />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/features" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminFeatures />
        </ProtectedRoute>
      } />
      <Route path="/admin/waitlist" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminWaitlist />
        </ProtectedRoute>
      } />
      <Route path="/admin/security-logs" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminSecurityLogs />
        </ProtectedRoute>
      } />
      <Route path="/admin/announcements" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminAnnouncements />
        </ProtectedRoute>
      } />
      <Route path="/admin/refunds" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminRefunds />
        </ProtectedRoute>
      } />
      <Route path="/admin/withdrawals" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminWithdrawals />
        </ProtectedRoute>
      } />
      <Route path="/admin/email-logs" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminEmailLogs />
        </ProtectedRoute>
      } />
      <Route path="/admin/demo-broadcast" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminDemoBroadcast />
        </ProtectedRoute>
      } />
      <Route path="/admin/terms" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminTerms />
        </ProtectedRoute>
      } />
      <Route path="/admin/privacy-policy" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminPrivacyPolicy />
        </ProtectedRoute>
      } />
      <Route path="/admin/system-health" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminSystemHealth />
        </ProtectedRoute>
      } />
      
      {/* Parent Routes */}
      <Route path="/parent" element={
        <ProtectedRoute allowedRoles={["parent"]}>
          <ParentDashboard />
        </ProtectedRoute>
      } />
      <Route path="/parent/children/add" element={
        <ProtectedRoute allowedRoles={["parent"]}>
          <AddChild />
        </ProtectedRoute>
      } />
      <Route path="/parent/child/:childId" element={
        <ProtectedRoute allowedRoles={["parent"]}>
          <ChildDetails />
        </ProtectedRoute>
      } />
      <Route path="/parent/reports" element={
        <ProtectedRoute allowedRoles={["parent"]}>
          <ParentReports />
        </ProtectedRoute>
      } />
      {/* /parent/add-child redirects to unified route */}
      <Route path="/parent/add-child" element={
        <ProtectedRoute allowedRoles={["parent"]}>
          <AddChild />
        </ProtectedRoute>
      } />
      <Route path="/parent/weekly-report" element={
        <ProtectedRoute allowedRoles={["parent"]}>
          <ParentWeeklyReport />
        </ProtectedRoute>
      } />
      <Route path="/parent/recordings" element={
        <ProtectedRoute allowedRoles={["parent"]}>
          <ParentRecordings />
        </ProtectedRoute>
      } />
      
      {/* Shared Routes (Students, Teachers & Parents) */}
      <Route path="/messages" element={
        <ProtectedRoute allowedRoles={["student", "teacher", "parent"]}>
          <UserMessages />
        </ProtectedRoute>
      } />
      <Route path="/profile/:userId" element={
        <ProtectedRoute allowedRoles={["student", "teacher", "parent", "admin", "child"]}>
          <PublicProfile />
        </ProtectedRoute>
      } />
      <Route path="/invoices" element={
        <ProtectedRoute allowedRoles={["student", "teacher", "parent"]}>
          <UserInvoices />
        </ProtectedRoute>
      } />
      <Route path="/complaints" element={
        <ProtectedRoute allowedRoles={["student", "teacher", "parent"]}>
          <SubmitComplaint />
        </ProtectedRoute>
      } />
      
      {/* Admin Complaints */}
      <Route path="/admin/complaints" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminComplaints />
        </ProtectedRoute>
      } />
      <Route path="/admin/ai-security" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminAISecurity />
        </ProtectedRoute>
      } />
      <Route path="/admin/security" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <SecurityCommandCenter />
        </ProtectedRoute>
      } />
      <Route path="/admin/email-logs" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <EmailLogsAdmin />
        </ProtectedRoute>
      } />
      <Route path="/admin/trust" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminTrustPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/bookings-v2" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminBookingsV2Page />
        </ProtectedRoute>
      } />
      
      {/* Virtual Classroom Routes */}
      <Route path="/classroom/:bookingId" element={
        <ProtectedRoute allowedRoles={["student", "teacher"]}>
          <VirtualClassroom />
        </ProtectedRoute>
      } />
      <Route path="/parent-monitor" element={<ParentMonitor />} />
      <Route path="/monitor/:bookingId" element={
        <ProtectedRoute allowedRoles={["admin", "parent"]}>
          <StealthMonitor />
        </ProtectedRoute>
      } />
      
      {/* Payment Routes */}
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
      <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

// ============================================
// BETA BANNER - يمكن إزالة هذا المكون عندما يصبح التطبيق جاهزاً
// ============================================
const BETA_BANNER_HEIGHT = 28;

const BetaBanner = ({ onVisibilityChange }) => {
  const [isVisible, setIsVisible] = useState(true);
  
  const handleClose = () => {
    setIsVisible(false);
    onVisibilityChange?.(false);
  };
  
  if (!isVisible) return null;
  
  return (
    <div 
      className="bg-emerald-600 text-white py-1 px-4 text-center text-xs font-light fixed top-0 left-0 right-0 z-[9999]" 
      style={{height: `${BETA_BANNER_HEIGHT}px`}}
    >
      <div className="container mx-auto flex items-center justify-center gap-2 h-full">
        <span>نسخة تجريبية — نعمل على تحسين تجربتك</span>
        <button 
          onClick={handleClose}
          className="mr-2 text-white/80 hover:text-white font-bold text-lg leading-none"
          aria-label="إغلاق"
        >
          ×
        </button>
      </div>
    </div>
  );
};
// ============================================

// ============================================
// TOUR WRAPPER - يعرض الجولة للمستخدمين الجدد
// ============================================
const TourWrapper = ({ children }) => {
  const { user } = useAuth();
  const [showTour, setShowTour] = useState(false);
  
  useEffect(() => {
    if (user && !localStorage.getItem('tourCompleted')) {
      // Show tour after a short delay
      const timer = setTimeout(() => setShowTour(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [user]);
  
  return (
    <>
      {children}
      {showTour && <InteractiveTour onComplete={() => setShowTour(false)} />}
    </>
  );
};

// ============================================

function App() {
  const [bannerVisible, setBannerVisible] = useState(true);

  // Coming-Soon gate removed — platform now publicly accessible worldwide.
  // (Set localStorage.setItem('access_granted','true') is no longer required.)

  return (
    <div className="App font-sans">
      {/* SpaceBackdrop removed per user request (May 13, 2026). The app
          now sits on a clean token-driven surface (bg-primary). The
          import is kept so the component is one line away if we ever
          decide to restore the cosmic backdrop. */}
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <ThemeProvider>
              <AccessibilityProvider>
              {/* BETA BANNER — Hidden for the competition showcase. Re-enable by uncommenting. */}
              {/* <BetaBanner onVisibilityChange={setBannerVisible} /> */}
              <FreePeriodBanner />
              <InactivityWarning />
              <div style={{ paddingTop: bannerVisible ? `${BETA_BANNER_HEIGHT}px` : '0' }}>
                <MaintenanceGuard>
                  <TourWrapper>
                    <Suspense
                      fallback={
                        <div className="min-h-screen flex items-center justify-center">
                          <div className="animate-spin w-10 h-10 border-2 border-indigo-400 border-t-transparent rounded-full" data-testid="route-suspense-spinner" />
                        </div>
                      }
                    >
                      <ChunkErrorBoundary>
                        <AppRoutes />
                      </ChunkErrorBoundary>
                    </Suspense>
                  </TourWrapper>
                </MaintenanceGuard>
              </div>
              <Toaster position="top-center" richColors />
              <TedChat />
              <LanguageGreetingBanner />
              <LockdownInterceptor>
                <></>
              </LockdownInterceptor>
              <MobileBackButton />
              <MobileBottomTabBar />
              <InstallAppPrompt />
              <KeyboardShortcutsBridge />
              <AccessibilityPanel />
              </AccessibilityProvider>
            </ThemeProvider>
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

// Bridge so KeyboardShortcutsOverlay can read the LanguageProvider context.
function KeyboardShortcutsBridge() {
  const { language } = useLanguage();
  return <KeyboardShortcutsOverlay isAr={language === "ar"} />;
}

export default App;
